import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  User,
  Mic,
  FileText,
  Users,
  Presentation,
  Award,
  TrendingUp,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Briefcase,
  GraduationCap,
  Building2,
  Headphones,
  Lightbulb,
  Target,
  BarChart3,
  Globe
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const CommunicationSkillsTraining = () => {
  const navigate = useNavigate();

  const pageData = {
    h1: 'Communication Skills Training in Chennai',
    metaTitle: 'Communication Skills Training in Chennai | Corporate & Professional Programs',
    metaDescription: 'Boost confidence, presentation, and workplace communication through ISDE\'s communication skills training in Chennai.',
    metaKeywords: 'communication skills training chennai, spoken english, presentation skills, business communication, public speaking training, corporate communication chennai',
    canonicalUrl: 'https://isde.academy/training/communication-skills-training-chennai'
  };

  const keyAreas = [
    {
      id: 'verbal',
      number: 1,
      title: 'Verbal Communication Skills',
      icon: MessageSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-500 to-cyan-600',
      description: 'Verbal communication is the foundation of effective interaction. Training focuses on clear articulation, voice modulation, professional vocabulary, and confidence in conversations.',
      topics: [
        'Clear articulation of ideas',
        'Voice modulation and tone',
        'Professional vocabulary and grammar',
        'Confidence in conversations and meetings'
      ],
      benefit: 'Strong verbal skills help professionals express ideas without hesitation or confusion.'
    },
    {
      id: 'non-verbal',
      number: 2,
      title: 'Non-Verbal Communication',
      icon: User,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-500 to-pink-600',
      description: 'Body language plays a critical role in communication. Training emphasizes eye contact, facial expressions, posture, and interpreting non-verbal cues.',
      topics: [
        'Eye contact and facial expressions',
        'Posture and gestures',
        'Body language awareness',
        'Interpreting non-verbal cues'
      ],
      benefit: 'Effective non-verbal communication enhances trust and credibility.'
    },
    {
      id: 'spoken-english',
      number: 3,
      title: 'Spoken English and Fluency',
      icon: Mic,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-500 to-teal-600',
      description: 'Many professionals seek communication skills training in Chennai to improve English fluency. Training includes sentence formation, pronunciation, and conversational practice.',
      topics: [
        'Sentence formation and pronunciation',
        'Reducing hesitation and fear',
        'Conversational English practice',
        'Professional spoken English'
      ],
      benefit: 'Improved fluency boosts confidence in professional and social settings.'
    },
    {
      id: 'business',
      number: 4,
      title: 'Business Communication Skills',
      icon: FileText,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Business communication is essential for corporate success. Training covers professional email writing, business reports, and client communication.',
      topics: [
        'Professional email writing',
        'Business reports and documentation',
        'Telephone and virtual communication etiquette',
        'Client and stakeholder communication'
      ],
      benefit: 'These skills ensure professionalism and clarity in the workplace.'
    },
    {
      id: 'presentation',
      number: 5,
      title: 'Presentation and Public Speaking Skills',
      icon: Presentation,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      gradient: 'from-rose-500 to-red-600',
      description: 'Presentation skills are vital for leadership and career growth. Training includes structuring presentations, overcoming stage fear, and audience engagement.',
      topics: [
        'Structuring effective presentations',
        'Overcoming stage fear',
        'Storytelling and audience engagement',
        'Handling questions confidently'
      ],
      benefit: 'Strong presentation skills improve visibility and leadership potential.'
    },
    {
      id: 'interpersonal',
      number: 6,
      title: 'Interpersonal Communication Skills',
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      gradient: 'from-indigo-500 to-purple-600',
      description: 'Interpersonal communication enables collaboration and teamwork. Training focuses on active listening, feedback, conflict resolution, and building rapport.',
      topics: [
        'Active listening techniques',
        'Giving and receiving feedback',
        'Conflict resolution',
        'Building rapport and trust'
      ],
      benefit: 'Effective interpersonal skills improve workplace relationships and team performance.'
    }
  ];

  const whoShouldAttend = [
    {
      title: 'College Students & Fresh Graduates',
      description: 'Prepare for campus placements, interviews, and workplace readiness',
      icon: GraduationCap,
      color: 'blue'
    },
    {
      title: 'Working Professionals & Executives',
      description: 'Improve job performance, presentation skills, and leadership communication',
      icon: Briefcase,
      color: 'emerald'
    },
    {
      title: 'Managers & Team Leaders',
      description: 'Enhance team communication, leadership presence, and decision-making',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'Sales & Customer Service',
      description: 'Build strong client relationships and improve customer satisfaction',
      icon: Headphones,
      color: 'amber'
    },
    {
      title: 'Healthcare & Education Professionals',
      description: 'Communicate effectively with patients, students, and stakeholders',
      icon: Building2,
      color: 'rose'
    },
    {
      title: 'Entrepreneurs & Business Owners',
      description: 'Lead teams, pitch ideas, and communicate with investors confidently',
      icon: Lightbulb,
      color: 'indigo'
    }
  ];

  const benefits = [
    {
      number: 1,
      title: 'Enhanced Career Growth',
      description: 'Strong communication skills improve job performance, promotion opportunities, and leadership effectiveness.',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      number: 2,
      title: 'Increased Confidence',
      description: 'Participants gain confidence in meetings, interviews, presentations, and daily conversations.',
      icon: Award,
      color: 'emerald'
    },
    {
      number: 3,
      title: 'Better Workplace Relationships',
      description: 'Clear and respectful communication reduces misunderstandings and conflicts.',
      icon: Users,
      color: 'purple'
    },
    {
      number: 4,
      title: 'Improved Customer and Client Interaction',
      description: 'Effective communication builds trust and improves customer satisfaction.',
      icon: Headphones,
      color: 'amber'
    },
    {
      number: 5,
      title: 'Strong Leadership Presence',
      description: 'Leaders with strong communication skills influence teams and drive results effectively.',
      icon: Target,
      color: 'rose'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Communication Skills Training in Chennai",
    "description": "Professional communication skills training covering verbal, non-verbal, business communication, presentation skills, and interpersonal effectiveness for workplace success.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "ISDE Academy",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      },
      "url": "https://isde.academy"
    },
    "courseCode": "COMM-SKILLS",
    "educationalLevel": "Professional Development",
    "teaches": [
      "Verbal Communication",
      "Non-Verbal Communication",
      "Spoken English and Fluency",
      "Business Communication",
      "Presentation and Public Speaking",
      "Interpersonal Communication"
    ],
    "availableLanguage": "English",
    "locationCreated": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Chennai",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is communication skills training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Communication skills training focuses on developing verbal, non-verbal, and written communication abilities that help individuals interact effectively in personal, academic, and professional environments. It includes techniques to convey messages clearly, listen actively, present confidently, and communicate professionally in different workplace situations."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend communication skills training in Chennai?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Communication skills training programs are suitable for college students and fresh graduates, working professionals and executives, managers and team leaders, sales and customer service professionals, healthcare and education professionals, and entrepreneurs and business owners."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageData.metaTitle}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.metaTitle} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.metaTitle} />
        <meta name="twitter:description" content={pageData.metaDescription} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <MessageSquare className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <Presentation className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Corporate & Professional Programs</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {pageData.h1}
              </h1>

              <p className="text-xl text-emerald-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                In today's fast-paced professional world, strong communication is no longer optional. It is a core skill that determines career growth, leadership success, and workplace effectiveness. Chennai, as a major hub for IT, manufacturing, healthcare, education, and corporate services, demands professionals who can express ideas clearly, listen actively, and communicate with confidence across teams and cultures.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 font-semibold px-8 py-6 text-lg"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* What Is Section */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">What Is Communication Skills Training?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Communication skills training focuses on developing verbal, non-verbal, and written communication abilities that help individuals interact effectively in personal, academic, and professional environments. It includes techniques to convey messages clearly, listen actively, present confidently, and communicate professionally in different workplace situations.
            </p>

            <p className="text-lg text-gray-700 mb-4 font-semibold">Communication skills training in Chennai typically covers:</p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                'Spoken English and fluency',
                'Verbal and non-verbal communication',
                'Presentation and public speaking',
                'Business and professional writing',
                'Interpersonal communication',
                'Active listening and feedback'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Important Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Why Communication Skills Training in Chennai Is Important</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              Chennai's workforce is diverse and competitive. Professionals often work in global teams, interact with international clients, and participate in cross-functional collaboration. Without effective communication, even technically skilled professionals may struggle to perform or advance.
            </p>

            <p className="text-lg text-gray-700 mb-4 font-semibold">Communication skills training in Chennai helps individuals to:</p>
            <div className="grid md:grid-cols-2 gap-4 mb-8 max-w-4xl">
              {[
                'Speak with clarity and confidence',
                'Improve workplace interactions',
                'Perform better in interviews and meetings',
                'Enhance leadership and team collaboration',
                'Build strong client and customer relationships'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <ChevronRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-700 italic max-w-4xl">
              Employers increasingly prioritize communication skills during recruitment and performance evaluations.
            </p>
          </div>
        </section>

        {/* Key Areas Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Key Areas Covered in Communication Skills Training</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {keyAreas.map((area, index) => {
                const IconComponent = area.icon;
                return (
                  <motion.article
                    key={area.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group">
                      <div className={`bg-gradient-to-br ${area.gradient} p-6 text-white`}>
                        <div className={`${area.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <IconComponent className={`h-8 w-8 ${area.color}`} />
                        </div>
                        <h4 className="text-2xl font-bold mb-2">
                          {area.number}. {area.title}
                        </h4>
                        <p className="text-white/90 text-sm leading-relaxed mb-4">
                          {area.description}
                        </p>
                      </div>
                      <CardContent className="p-6 bg-white">
                        <h5 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">Key Topics:</h5>
                        <ul className="space-y-2 mb-4">
                          {area.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 ${area.color} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-600 text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                        <div className={`${area.bgColor} p-3 rounded-lg mt-4`}>
                          <p className={`text-sm font-medium ${area.color}`}>{area.benefit}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who Should Attend */}
        <section className="bg-gradient-to-br from-emerald-50 to-teal-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Who Should Attend Communication Skills Training in Chennai?</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {whoShouldAttend.map((audience, index) => {
                const IconComponent = audience.icon;
                const colors = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                  rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
                  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' }
                };
                const color = colors[audience.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`h-full bg-white border-2 ${color.border} hover:shadow-xl transition-all hover:-translate-y-2`}>
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-3 text-gray-900">{audience.title}</h5>
                        <p className="text-gray-600 text-sm">{audience.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-center text-gray-700 max-w-3xl mx-auto">
              Whether you are starting your career or aiming for leadership roles, communication skills training adds long-term value.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Benefits of Communication Skills Training in Chennai</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                const colors = {
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
                  rose: { bg: 'bg-rose-50', text: 'text-rose-600' }
                };
                const color = colors[benefit.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`${color.bg} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-2 text-gray-900">{benefit.number}. {benefit.title}</h5>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Corporate Training */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-emerald-50">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Corporate Communication Skills Training in Chennai</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              Organizations invest in corporate communication skills training in Chennai to improve employee performance and organizational culture. Corporate programs are customized to:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Building2, title: 'Industry-specific communication needs', color: 'emerald' },
                { icon: Target, title: 'Leadership and managerial roles', color: 'blue' },
                { icon: Headphones, title: 'Customer-facing interactions', color: 'purple' },
                { icon: BarChart3, title: 'Internal communication effectiveness', color: 'amber' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600' }
                };
                const color = colors[item.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-white hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 max-w-3xl">
              These programs include workshops, role plays, case studies, and real-life business scenarios.
            </p>
          </div>
        </section>

        {/* Training for Students */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Communication Skills Training for Students in Chennai</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              Communication skills training for students in Chennai prepares learners for:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: GraduationCap, title: 'Campus placements and interviews', color: 'blue' },
                { icon: Users, title: 'Group discussions', color: 'emerald' },
                { icon: Presentation, title: 'Professional presentations', color: 'purple' },
                { icon: Briefcase, title: 'Workplace readiness', color: 'amber' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  amber: { bg: 'bg-amber-50', text: 'text-amber-600' }
                };
                const color = colors[item.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 flex items-center gap-4">
                        <div className={`${color.bg} p-3 rounded-lg flex-shrink-0`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 text-lg italic max-w-3xl">
              Students with strong communication skills gain a competitive advantage in the job market.
            </p>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="bg-gradient-to-br from-emerald-50 to-cyan-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Why Choose us as your Professional Communication Skills Training in Chennai?</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              Our training institute offer:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { icon: Award, title: 'Certified and experienced trainers' },
                { icon: BookOpen, title: 'Industry-relevant curriculum' },
                { icon: Users, title: 'Practical learning through activities' },
                { icon: BarChart3, title: 'Personalized feedback and assessment' },
                { icon: TrendingUp, title: 'Measurable improvement in communication skills' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-white hover:shadow-lg transition-shadow h-full">
                      <CardContent className="p-6 text-center">
                        <div className="bg-emerald-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                          <IconComponent className="h-7 w-7 text-emerald-600" />
                        </div>
                        <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Future Scope */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Future Scope of Communication Skills Training in Chennai</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              As workplaces become more global and digital, communication skills will remain one of the most in-demand competencies. Our ISDE Academy's Communication skills training in Chennai will continue to support:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Target, text: 'Leadership development' },
                { icon: TrendingUp, text: 'Career transitions' },
                { icon: BarChart3, text: 'Corporate performance improvement' },
                { icon: Award, text: 'Professional excellence' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6 flex items-center gap-3">
                        <IconComponent className="h-8 w-8 text-emerald-600 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{item.text}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Boost Your Communication Skills?
              </h2>
              <p className="text-xl text-emerald-100 mb-8">
                Join our comprehensive communication skills training in Chennai and enhance your confidence, presentation abilities, and workplace effectiveness
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 font-semibold px-8 py-6 text-lg"
                >
                  View Training Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommunicationSkillsTraining;
